import React, { useEffect, useState, useContext } from "react";
import apiCalls from "shared/utils/apiCalls";
import { useParams, useHistory } from "react-router-dom";
import Tags from "shared/components/Tags/Tags";

import Grid from "@material-ui/core/Grid";
import MUButton from "@material-ui/core/Button";

import styles from "./ChallengeDetails.module.scss";
import Modal from "shared/components/Modal/Modal";
import { CheckCircleRounded, SaveAltRounded } from "@material-ui/icons";
import { Challenge } from "shared/types/challenges";

import AppContext from "context/AppContext";
import Button from "shared/components/Buttons/Button";
import wrapLocalLoader from "shared/utils/wrapLocalLoader";
import ReactMarkdown from "react-markdown";
import CodeBlock from "shared/components/CodeBlock/CodeBlock";
import ChallengeDetailsSkeleton from "./ChallengeDetailsSkeleton/ChallengeDetailsSkeleton";
import ErrorMessage from "shared/components/ErrorMessage/ErrorMessage";
import { Errors } from "shared/types/errors";
import { download } from "shared/utils/download";
import SuccessMessage from "shared/components/SuccessMessage/SuccessMessage";
import { Success } from "shared/types/success";
import ImageSlideshow from "shared/components/ImageSlideshow/ImageSlideshow";
import figmaLogo from "shared/icons/Logo/figma.png";
import NotFound from "shared/components/NotFound/NotFound";
import firebase from "firebase/app";

const ChallengeDetails = () => {
  const { state, dispatch } = useContext(AppContext);

  const [modalOpen, setModalOpen] = useState(false);
  const [requireLoginModalOpen, setRequireLoginModalOpen] = useState(false);
  const [startedModalOpen, setStartedModalOpen] = useState(false);

  const [
    permissionDeclinedModalOpen,
    setPermissionDeclinedModalOpen,
  ] = useState(false);

  const [solutionLoading, setSolutionLoading] = useState(false);

  const [challenge, setChallenge] = useState<Challenge>();
  const [notFound, setNotFound] = useState<boolean>();
  const [activeSolutions, setActiveSolutions] = useState<any>();
  const [solution, setSolution] = useState<any>(); // TODO solution type

  const history = useHistory();

  const { id } = useParams();

  const getUserSolutions = async (filter: "inProgress" | "submitted") => {
    return await apiCalls({
      method: "get",
      url: `/solutions/user/${state.user.uid}?filter=${filter}`,
    });
  };

  const getSolutions = async () => {
    const activeSolutionRes = await getUserSolutions("inProgress");

    if (activeSolutionRes) {
      setActiveSolutions(activeSolutionRes.data);
    }
  };

  useEffect(() => {
    if (!state.user) {
      return;
    }

    getSolutions();

    // eslint-disable-next-line
  }, [state.user]);

  const getSolution = wrapLocalLoader(async () => {
    const res = await apiCalls(
      {
        method: "get",
        url: `/solutions/challenge/${id}`,
      },
      dispatch
    );

    if (res) {
      setSolution(res.data);
    } else {
      setSolution(undefined);
    }
  }, setSolutionLoading);

  useEffect(() => {
    const getChallenge = async () => {
      const res = await apiCalls(
        {
          method: "get",
          url: `/challenges/${id}`,
        },
        dispatch
      );

      if (res) {
        setChallenge(res.data);
      } else {
        setNotFound(true);
        return;
      }
    };

    if (!challenge) {
      getChallenge();
    }

    if (state.user) {
      getSolution();
    }

    if (state.user) {
      setRequireLoginModalOpen(false);
    }

    // eslint-disable-next-line
  }, [id, state.user]);

  const startSolution = wrapLocalLoader(async () => {
    if (!state.user) {
      setRequireLoginModalOpen(true);
      return;
    }

    if (state.user.reputations < 0) {
      setPermissionDeclinedModalOpen(true);
      return;
    }

    if (!activeSolutions) {
      await getSolutions();
    }

    if (activeSolutions && activeSolutions.length > 4) {
      setPermissionDeclinedModalOpen(true);
      return;
    }

    const res = await apiCalls(
      {
        method: "post",
        data: { uid: state.user.uid, challengeId: challenge?.id },
        url: `/solutions`,
      },
      dispatch
    );

    firebase.analytics().logEvent("challenges_started");

    if (res) {
      if (hasResources()) {
        downloadResource();
      }

      setStartedModalOpen(true);

      await getSolution();
    }
  }, setSolutionLoading);

  const downloadResource = () => {
    if (!challenge?.resourceId || challenge?.resourceId === "null") {
      return;
    }

    download({
      link: `https://github.com/devchallenges/${challenge?.resourceId}/archive/master.zip`,
    });
  };

  const redirectToDesign = (designId: string) => {
    if (!state.user) {
      setRequireLoginModalOpen(true);
      return;
    }

    window.open(`https://www.figma.com/file/${designId}`);
  };

  if (notFound) {
    return <NotFound />;
  }

  if (!challenge) {
    return <ChallengeDetailsSkeleton />;
  }

  const hasResources = () => {
    return challenge.resourceId !== "null";
  };

  const { tags, name, brief, images, designId } = challenge;

  return (
    <>
      <div>
        <ImageSlideshow
          images={images}
          open={modalOpen}
          closeModal={() => setModalOpen(false)}
        />
        <Modal
          open={requireLoginModalOpen}
          closeModal={() => setRequireLoginModalOpen(false)}
          size="sm"
        >
          <ErrorMessage errorType={Errors.requireLogin} />
        </Modal>

        <Modal
          open={permissionDeclinedModalOpen}
          closeModal={() => setPermissionDeclinedModalOpen(false)}
          size="sm"
        >
          <ErrorMessage errorType={Errors.reputationsDeclined} />
        </Modal>

        <Modal
          open={startedModalOpen}
          closeModal={() => setStartedModalOpen(false)}
          size="sm"
        >
          <SuccessMessage
            successType={Success.challengeStarted}
            buttons={
              <>
                <div style={{ marginRight: "var(--space-sm)" }}>
                  <Button
                    onClick={() => setStartedModalOpen(false)}
                    aria-label="got it"
                  >
                    Got it!
                  </Button>
                </div>

                <Button
                  aria-label="submit solution"
                  loading={solutionLoading}
                  buttonVariant="secondary"
                  onClick={() =>
                    history.push(
                      `/challenges/${challenge.id}/submit-solution/${solution.id}`
                    )
                  }
                >
                  Submit Solution
                </Button>
              </>
            }
          />
        </Modal>

        {designId && (
          <div className={styles.challengeDetails_iframe}>
            <iframe
              id="figmaIframe"
              title="design"
              src={`
              https://www.figma.com/embed?embed_host=astra&url=https%3A%2F%2Fwww.figma.com%2Ffile%2F${designId}%2F404-page%3Fnode-id%3D0%253A1&chrome=DOCUMENTATION`}
            ></iframe>
          </div>
        )}

        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={8}>
            <div style={{ display: "flex" }}>
              <h1>{name}</h1>
              {state.user && state.user.role === "admin" && (
                <MUButton
                  variant="text"
                  type="button"
                  style={{
                    marginBottom: "var(--space-md)",
                    textTransform: "lowercase",
                    color: "var(--primary-color-light)",
                    fontFamily: "var(--heading-font-family)",
                  }}
                  size="small"
                  onClick={() => history.push(`/challenges/${id}/edit`)}
                >
                  Edit
                </MUButton>
              )}
            </div>
            <div className={styles.challengeDetails_tagsAndLevel}>
              {tags && <Tags tags={tags} />}

              {/* <p className={styles.challengeDetails_tagsAndLevel_level}>
              {proficientLevels[level]}
              </p> */}
            </div>
            {brief && (
              <div className={styles.challengeDetails_description}>
                <ReactMarkdown
                  renderers={{ code: CodeBlock }}
                  source={brief}
                ></ReactMarkdown>

                <p>
                  As long as you fulfill all the user stories, you can give your
                  personal touches by adding transition, using your own images,
                  changing colors, or even creating your own layout,...
                </p>

                <p>
                  Remember to put your name on the footer to prevent other from
                  submitting your solutions.
                </p>

                <p>
                  Once you completed, submit your solutions by providing{" "}
                  <b>URLs for both GitHub repository and live app</b> on any
                  hosting platform (
                  <a
                    href="https://blogs.devchallenges.io/posts/tJ26U8MhZTPgBSRSwpqr"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    5 Free Hosting Platform
                  </a>
                  ) and explain briefly what you have done. You can check
                  others’ solutions and give them feedbacks.
                </p>

                <p>
                  Share your solutions with others with #devchallenges and tag{" "}
                  <a
                    href="https://twitter.com/devchallengesio"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    @devchallengesio
                  </a>{" "}
                  on Twitter.
                </p>
              </div>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <div className={styles.challengeDetails_instructions}>
              <h4>How to start</h4>
              <ol>
                <li>Read the challenge's details</li>
                <li>
                  {`Start the challenge ${
                    hasResources() ? `and download the resources` : ""
                  }`}{" "}
                  {hasResources() && solution && !solution.submitted && (
                    // eslint-disable-next-line
                    <a
                      href="#"
                      style={{
                        color: "var(--grey-5)",
                        marginLeft: "var(--space-2xs)",
                      }}
                      onClick={downloadResource}
                    >
                      <SaveAltRounded fontSize="inherit" />
                    </a>
                  )}
                </li>
                <li>Check designs on Figma</li>
                <li>Have fun coding!!</li>
              </ol>

              <div className={styles.challengeDetails_buttons}>
                {!solution ? (
                  <div onClick={startSolution}>
                    <Button
                      aria-label="start"
                      loading={solutionLoading}
                      disabled={!state.user}
                      textTransform="capitalize"
                    >
                      {`Start ${hasResources() ? `and download` : ""}`}
                    </Button>
                  </div>
                ) : !solution.submitted ? (
                  <Button
                    aria-label="submit"
                    loading={solutionLoading}
                    buttonVariant="secondary"
                    onClick={() =>
                      history.push(
                        `/challenges/${challenge.id}/submit-solution/${solution.id}`
                      )
                    }
                    textTransform="capitalize"
                  >
                    Submit Solution
                  </Button>
                ) : (
                  <>
                    <Button
                      aria-label="completed"
                      textTransform="capitalize"
                      buttonVariant="default"
                      startIcon={
                        <CheckCircleRounded
                          color="inherit"
                          style={{ color: "var(--green-dark)" }}
                        />
                      }
                      onClick={() =>
                        history.push(`/solutions?challengeId=${challenge.id}`)
                      }
                    >
                      View solutions
                    </Button>
                  </>
                )}

                {solution && (
                  <div className={styles.challengeDetails_buttons_designButton}>
                    <Button
                      aria-label="design"
                      loading={solutionLoading}
                      buttonVariant="default"
                      onClick={() => redirectToDesign(designId)}
                      textTransform="capitalize"
                    >
                      <img
                        loading="lazy"
                        src={figmaLogo}
                        alt="figma-logo"
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "var(--rounded-sm)",
                          marginRight: "var(--space-xs)",
                        }}
                      />
                      Design
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default ChallengeDetails;
